import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import {
    State,
    Product,
    Substitute,
    VendorProduct,
} from 'admin/stores/redux/AdminProduct/interfaces';
import { initialProduct } from 'admin/stores/redux/AdminProduct/constants';
import { Attribute } from 'common/components/product/productUtils';

const initialState: State = {
    initialized: false,
    product: initialProduct,
    vendorProducts: [],
};

export const adminProductSlice = createSlice({
    name: 'adminProduct',
    initialState,
    reducers: {
        initialize: (
            state: State,
            action: PayloadAction<{
                product: Product;
                vendorProducts: VendorProduct[];
            }>
        ) => {
            const { product, vendorProducts } = action.payload;

            state.initialized = true;
            state.product = {
                ...product,
                attributes: product?.attributes || [],
            };
            state.vendorProducts = vendorProducts;
        },

        setProduct: (state: State, action: PayloadAction<Product>) => {
            const currentProduct = { ...state.product };
            state.product = {
                ...currentProduct,
                ...action.payload,
            };
        },

        setVendorProducts: (
            state: State,
            action: PayloadAction<VendorProduct[]>
        ) => {
            state.vendorProducts = action.payload;
        },

        addAttribute: (state: State, action: PayloadAction<Attribute>) => {
            state.product = {
                ...state.product,
                attributes: [
                    ...(state.product.attributes ?? []),
                    action.payload,
                ],
            };
        },

        deleteAttribute: (state: State, action: PayloadAction<number>) => {
            const attributes = [...(state.product.attributes ?? [])]?.filter(
                attribute => attribute?.id !== action.payload
            );

            state.product = {
                ...state.product,
                attributes,
            };
        },

        updateAttribute: (state: State, action: PayloadAction<Attribute>) => {
            if (!state.product?.attributes) return;

            const attributeIndex = state.product.attributes.findIndex(
                attribute => attribute.id === action.payload.id
            );

            if (attributeIndex === -1) {
                state.product.attributes.push(action.payload);
            } else {
                state.product.attributes[attributeIndex] = action.payload;
            }
        },

        updateSubstitutionOptions: (
            state: State,
            action: PayloadAction<{ allowSub: boolean; forceSub: boolean }>
        ) => {
            const { allowSub, forceSub } = action.payload;

            state.product = {
                ...state.product,
                allowSub,
                forceSub,
            };
        },

        addSubstitution: (state: State, action: PayloadAction<Substitute>) => {
            const substitutes = [
                ...(state.product.substitutes ?? []),
                action.payload,
            ];

            state.product = {
                ...state.product,
                substitutes,
            };
        },

        deleteSubstitution: (state: State, action: PayloadAction<number>) => {
            const substitutes = [...(state.product.substitutes ?? [])].filter(
                item => item.id !== action.payload
            );

            state.product = {
                ...state.product,
                substitutes,
            };
        },

        replaceProductAndVendorProducts: (
            state: State,
            action: PayloadAction<{
                product: Product;
                vendorProducts: VendorProduct[];
            }>
        ) => {
            const { product, vendorProducts } = action.payload;

            state.product = product;
            state.vendorProducts = vendorProducts;
        },
    },
});

export const {
    initialize,
    setProduct,
    setVendorProducts,
    addAttribute,
    updateAttribute,
    deleteAttribute,
    updateSubstitutionOptions,
    addSubstitution,
    deleteSubstitution,
    replaceProductAndVendorProducts,
} = adminProductSlice.actions;

export default adminProductSlice.reducer;
